import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { AddressLookup, AddressLookupResponse, AddressLookupSummary, GoAddressNewAddressRes } from '../models';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root',
})
export class AddressLookupService {
  protected readonly httpService = inject(HttpClient);
  private readonly configService = inject(ConfigService);

  baseUri = 'https://portal.goaddress.io/';
  private cachedAddresses: GoAddressNewAddressRes[] = [];

  getAddresses(search: string): Observable<AddressLookupSummary[]> {
    return this.httpService
      .get<AddressLookupResponse>(`${this.baseUri}api/address/search?q=${encodeURIComponent(search)}`, {
        headers: this.authHeaders,
      })
      .pipe(
        tap((response: AddressLookupResponse) => {
          this.cachedAddresses = response.new_address_res || [];
        }),
        map((response: AddressLookupResponse) =>
          (response.results || []).map((result, index) => ({
            id: index.toString(),
            address: result.label,
            url: index.toString(),
          })),
        ),
      );
  }

  getAddressDetails(index: string): Observable<AddressLookup> {
    const i = parseInt(index, 10);
    const cached = this.cachedAddresses[i];
    if (!cached) {
      return of({} as AddressLookup);
    }
    return of(this.mapToAddressLookup(cached));
  }

  private get authHeaders(): HttpHeaders {
    return new HttpHeaders({
      Authorization: `Bearer ${this.configService.config.addressLookupApiKey}`,
    });
  }

  private mapToAddressLookup(res: GoAddressNewAddressRes): AddressLookup {
    const lines: string[] = [];
    if (res.flat) {
      lines.push(res.flat);
    }
    if (res.building_name) {
      lines.push(res.building_name);
    }

    const road = res.road || res.street || '';
    const buildingHasHouseNo =
      res.building_name &&
      res.house_no &&
      (res.building_name.startsWith(res.house_no + ' ') || res.building_name === res.house_no);

    if (res.house_no && road) {
      if (road.startsWith(res.house_no + ' ') || buildingHasHouseNo) {
        lines.push(road);
      } else {
        lines.push(res.house_no + ' ' + road);
      }
    } else if (road) {
      lines.push(road);
    } else if (res.house_no && !buildingHasHouseNo) {
      lines.push(res.house_no);
    }

    const locality = res.town && res.town.toLowerCase() !== (res.post_town || '').toLowerCase() ? res.town : '';

    return {
      line_1: lines[0] || '',
      line_2: lines[1] || '',
      line_3: lines[2] || '',
      line_4: lines[3] || '',
      locality,
      town_or_city: res.post_town || '',
      county: res.county || '',
      country: res.country || '',
      postcode: res.postcode || '',
    };
  }
}
